<template>
  <Panel
    class="suicide-modal custom-scrollbar position-relative"
    :variant="['transparent']"
  >
    <Title :text="$t('_web_account-remove', 'Remove account')" />
    <span
      class="position-absolute mx-4 px-3 close-icon cursor-hand"
      @click="closeModal"
    >
      <Icon name="close" width="16" :fill="$scss.primaryTextBg" />
    </span>

    <div v-if="actionConfirmed">
      <TextBlock class="my-5" tag="p">
        {{
          $t('_web_account-remove-code-sent', 'Code has been sent to {email}', {
            email: partHiddenEmailOrPhone,
          })
        }}
      </TextBlock>

      <div class="mt-4 verify-code-footer text-muted font-size-base">
        <p>
          {{
            account.email
              ? $$t(
                  'Please, enter confirmation code here. We have sent it to your email:',
                  'footer-text-email'
                )
              : $$t(
                  'Please, enter confirmation code here. We have sent SMS to your phone:',
                  'footer-text-phone'
                )
          }}
        </p>

        <InputVerify
          v-model="code"
          class="confirmation-code mt-5 mb-5"
          tag="form"
          :count="$options.verifyCodeCount"
          @error="formError"
        />
        <div
          class="mt-5 text-danger text-center"
          v-text="errorText || errorRequest"
        />

        <ResendCode
          v-bind="resendCodeBind"
          :action="sendCode"
          :action-data="identityData"
          @resended="refreshCode"
        />
      </div>
      <div class="mt-5">
        <Btn
          :disabled="code.length < 4"
          class="btn-primary mx-2"
          :class="{ 'is-loading': isLoading }"
          :label="$t('_web_account-remove', 'Remove account')"
          @click="formSubmit"
        />
        <Btn
          class="btn-default mx-2"
          :class="{ 'is-loading': isLoading }"
          :label="$t('_web_profile-cancel_update', 'Cancel')"
          @click="closeModal"
        />
      </div>
    </div>
    <div v-if="!actionConfirmed">
      <TextBlock class="mb-5" tag="p">
        {{
          $t(
            '_web_account-remove-description',
            'We will send you email with code, when you provide code your account will be removed entirely from the system, and you cannot restore it.'
          )
        }}
      </TextBlock>
      <TextBlock class="mb-5" tag="p">
        {{
          $t(
            '_web_account-remove-description-new-account',
            'You will be able to register a new account with the same credentials.'
          )
        }}
      </TextBlock>
      <invisible-recaptcha
        class="btn-primary-delete"
        :sitekey="recaptchaKey"
        :validate="() => sendDeleteRequest"
        :callback="handleRecaptchaResponse"
        >{{ $t('_web_post_delete-confirm', 'Yes, delete') }}
      </invisible-recaptcha>
    </div>
    <ModalBackdrop
      v-model="isLoading"
      class="verify-code-loader"
      :style="{ width: `100%` }"
    >
      <Loader />
    </ModalBackdrop>
    <div></div>
  </Panel>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { translate } from '@/mixins/translate';
import { RECAPTCHA_KEY } from '@/api/config';
import isLoading from '@/mixins/isLoading';
import InputVerify from '@/components/form/InputVerify';
import Loader from '@/components/common/Loader';
import ModalBackdrop from '@/components/common/ModalBackdrop';
import ResendCode from '@/components/text/ResendCode';
import Title from '@/components/atoms/Title';
import Panel from '@/components/atoms/Panel';
import TextBlock from '@/components/atoms/TextBlock';
import Icon from '@/components/atoms/Icon';
import Btn from '@/components/atoms/HeraFormBtn';
import InvisibleRecaptcha from 'vue-invisible-recaptcha';

export default {
  name: 'RemoveAccountModal',
  components: {
    Title,
    InputVerify,
    ModalBackdrop,
    ResendCode,
    Panel,
    Loader,
    TextBlock,
    Btn,
    Icon,
    'invisible-recaptcha': InvisibleRecaptcha,
  },
  inject: ['$validator'],
  mixins: [isLoading, translate('_web_verify-code')],
  data() {
    return {
      actionConfirmed: false,
      recaptchaKey: RECAPTCHA_KEY,
      code: '',
      errorRequest: null,
      errorText: null,
      resendTimer: 0,
      intervalTimer: 0,
      sectionWidth: 0,
    };
  },
  computed: {
    ...mapState('my', ['account']),
    identityData() {
      return {
        email: this.account.email ? this.account.email : null,
        phone: this.account.phone ? this.account.phone : null,
      };
    },
    partHiddenEmailOrPhone() {
      if (!this.account.email && !this.account.phone) return;
      return this.account.email
        ? this.account.email.replace(/(\w{1})[\w.-]+@([\w.]+\w)/, '$1***@$2')
        : this.account.phone.slice(0, 3) +
            '*****' +
            this.account.phone.slice(-3, -3) +
            this.account.phone.slice(-2, this.account.phone.length);
    },
    resendCodeBind() {
      return {
        timeoutTextTranslateKey: this.$$tCreateKey('timeout-text_email'),
        timeoutTextDefault:
          'We got your request. Re-send button will be available in {timer} sec.',
        resendTextTranslateKey: this.$$tCreateKey('retry-text_email'),
        resendTextDefault:
          "If you didn't get e-mail, try to {:resend} email or check spam folder.",
      };
    },
  },
  beforeRouteLeave(from, next) {
    // routerNameTo = name;
    this.errors.clear();
    next();
  },
  created() {
    this.$options.verifyCodeCount = 4;
  },
  methods: {
    ...mapActions([
      'fetchIdentityInfo',
      'sendCode',
      'verifyUser',
      'deleteProfileRequest',
      'deleteProfileSubmit',
      'closeModal',
      'errorNotify',
    ]),
    handleRecaptchaResponse(captcha_response) {
      this.recaptchaResponse = captcha_response;
      this.sendDeleteRequest(captcha_response);
    },
    sendDeleteRequest(captcha_response) {
      this.isLoading = true;

      this.fetchIdentityInfo()
        .then(() => {
          this.deleteProfileRequest()
            .then(() => {
              this.sendCode({
                email: this.account.email ? this.account.email : null,
                phone: this.account.phone ? this.account.phone : null,
                deleting: true,
                captcha_response: captcha_response,
              })
                .then(() => {
                  this.actionConfirmed = true;
                })
                .catch(this.errorNotify);
            })
            .catch(this.errorNotify);
        })
        .catch(this.errorNotify)
        .finally(() => {
          this.isLoading = false;
        });
    },
    formSubmit() {
      this.setLoading();
      this.errorRequest = null;

      this.$validator
        .validateAll()
        .then(
          result =>
            result
              ? Promise.resolve({
                  code: this.code,
                  ...this.identityData,
                })
              : Promise.reject() // eslint-disable-line
        )
        .then(this.verifyUser)
        .then(this.deleteProfileSubmit)
        .then(this.unsetLoading)
        .then(() => {
          this.closeModal();
          this.$router.push({ name: 'signout' });
        })
        .catch(err => {
          this.unsetLoading();
          this.errorRequest = err.message || err;
          const from = this.$route.params.from;
          if (
            err === this.$t('_web_session_expired_error', 'Session expired') &&
            from
          ) {
            this.errorNotify(err);
            this.$nextTick(() => this.$router.push({ name: from }));
          }
        });
    },
    formError(err) {
      this.errorText = err;
    },
    refreshCode() {
      this.code = '';
    },
  },
};
</script>
<style scoped lang="scss">
.suicide-modal {
  max-height: 100vh;
  max-width: 567px;
  width: 100%;

  .close-icon {
    @include auto-rtl(right, 0);
    top: $ph-large-space;
  }
  .confirmation-code {
    width: 70%;
  }

  &-loader {
    @include auto-rtl(
      (
        left: unset,
        right: 0,
      )
    );
    .modalbackdrop_container {
      width: unset;
    }
  }
}
.btn-primary-delete {
  background: var(--ph-main) !important;
  position: relative;
  outline: none;
  border: none;
  border-radius: 20px;
  background: transparent;
  color: var(--ph-primary-text);
  border-radius: 5px;
  padding: 0.5rem 1.5rem 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  overflow: hidden;
}
</style>
